import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDDropzone from "components/MDDropzone";
import HelpTooltip from "components/HelpTooltip";
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import DataTable from "components/patterns/DataTable";
import FormField from "components/FormField";
import { useState, useEffect, useMemo } from "react";
import { ApiError, createApiClient } from "common/apiClient";
import { serverBaseURL } from "common/settings";
import {
  estimateBill,
  estimateBasicFee,
  estimateUploadData,
  estimateDataTransfer,
} from "common/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

const RUNNING_DATA_ACCEPTED_FILES = ".csv";
const RUNNING_DATA_MAX_FILE_SIZE_MB = 20;

function SiteManager() {
  const { t, i18n } = useTranslation("pages");
  const { t: tHelp } = useTranslation("help");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [quotationOpen, setQuotationOpen] = useState(false);
  const [siteNumber, setSiteNumber] = useState(0);
  const [dayNumber, setDayNumber] = useState(1000);

  const [currentEditSiteId, setCurrentEditSiteId] = useState(0);
  const [siteName, setSiteName] = useState("");
  const [country, setCountry] = useState("");
  const [coordinate, setCoordinate] = useState("");
  const [installationDateNew, setInstallationDateNew] = useState("");
  const [installationDateUsed, setInstallationDateUsed] = useState("");
  const [panelNumber, setPanelNumber] = useState(0);
  const [panelPrice, setPanelPrice] = useState(0);
  const [outputPerPanel, setOutputPerPanel] = useState(0);
  const [panelOutput, setPanelOutput] = useState("");
  const [timezone, setTimezone] = useState("Etc/GMT+7");
  const [inputTimezone, setInputTimezone] = useState("");
  const [powerDcRated, setPowerDcRated] = useState(1000);
  const [gammaPdc, setGammaPdc] = useState(-0.005);
  const [tilt, setTilt] = useState(40);
  const [azimuth, setAzimuth] = useState(180);
  const [mu, setMu] = useState(0.8);
  const [runningDataFiles, setRunningDataFiles] = useState({
    columns: [
      { Header: t("siteManager.fileName"), accessor: "name" },
      { Header: t("siteManager.action"), accessor: "action", width: "10%" },
    ],

    rows: [],
  });
  const [generateFinished, setGenerateFinished] = useState(false);
  const [showAddSiteAlert, setShowAddSiteAlert] = useState(false);
  const [filePendingDelete, setFilePendingDelete] = useState("");
  const [deletingFile, setDeletingFile] = useState(false);

  const handleFinishOpen = () => setGenerateFinished(true);
  const handleFinishClose = () => setGenerateFinished(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);
  const { getToken } = useKindeAuth();
  const [requestError, setRequestError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const apiClient = useMemo(() => createApiClient({ getToken }), [getToken]);
  const systemType = "ongrid";

  const timezoneOptions = [
    "Etc/GMT",
    "Etc/GMT+0",
    "Etc/GMT+1",
    "Etc/GMT+2",
    "Etc/GMT+3",
    "Etc/GMT+4",
    "Etc/GMT+5",
    "Etc/GMT+6",
    "Etc/GMT+7",
    "Etc/GMT+8",
    "Etc/GMT+9",
    "Etc/GMT+10",
    "Etc/GMT+11",
    "Etc/GMT+12",
    "Etc/GMT-0",
    "Etc/GMT-1",
    "Etc/GMT-2",
    "Etc/GMT-3",
    "Etc/GMT-4",
    "Etc/GMT-5",
    "Etc/GMT-6",
    "Etc/GMT-7",
    "Etc/GMT-8",
    "Etc/GMT-9",
    "Etc/GMT-10",
    "Etc/GMT-11",
    "Etc/GMT-12",
    "Etc/GMT-13",
    "Etc/GMT-14",
    "Etc/GMT0",
    "Etc/Greenwich",
    "Etc/UCT",
    "Etc/UTC",
    "Etc/Universal",
    "Etc/Zulu",
  ];
  const [tableData, setTableData] = useState({
    columns: [
      { Header: t("siteManager.name"), accessor: "name" },
      { Header: t("siteManager.country"), accessor: "country" },
      { Header: t("siteManager.coordinate"), accessor: "coordinate" },
      { Header: t("siteManager.installationDateNew"), accessor: "installation_date_new" },
      { Header: t("siteManager.installationDateUsed"), accessor: "installation_date_used" },
      { Header: t("siteManager.panelOutput"), accessor: "panel_output" },
      { Header: t("siteManager.timezone"), accessor: "timezone" },
      { Header: t("siteManager.powerDcRated"), accessor: "power_dc_rated" },
      { Header: t("siteManager.gammaPdc"), accessor: "gamma_pdc" },
      { Header: t("siteManager.tilt"), accessor: "tilt" },
      { Header: t("siteManager.azimuth"), accessor: "azimuth" },
      { Header: t("siteManager.mu"), accessor: "mu" },
      { Header: t("siteManager.action"), accessor: "actions" },
    ],

    rows: [],
  });

  const handleCreateOpen = () => {
    handleCreateClose();
    setCurrentEditSiteId("");
    setSiteName("");
    setCountry("");
    setCoordinate("");
    setInstallationDateNew("");
    setInstallationDateUsed("");
    setPanelNumber(0);
    setPanelPrice(0);
    setOutputPerPanel(0);
    setPanelOutput(0);
    setTimezone("Etc/GMT+7");
    setPowerDcRated(1000);
    setGammaPdc(-0.005);
    setTilt(40);
    setAzimuth(180);
    setMu(0.8);
    setCreateOpen(true);
  };
  const handleCreateClose = () => setCreateOpen(false);
  const handleEditOpen = () => setEditOpen(true);
  const handleEditClose = () => setEditOpen(false);
  const handleQuotationOpen = () => setQuotationOpen(true);
  const handleQuotationClose = () => setQuotationOpen(false);
  const handleDeleteFileOpen = (file) => setFilePendingDelete(file);
  const handleDeleteFileClose = () => {
    if (!deletingFile) {
      setFilePendingDelete("");
    }
  };

  async function requestWithFeedback(request) {
    setRequestError("");

    try {
      return await request();
    } catch (error) {
      setRequestError(
        error instanceof ApiError
          ? error.message
          : "Unable to complete the request. Please try again."
      );
      return undefined;
    }
  }

  async function createSite() {
    if (siteName === "" || country === "" || coordinate === "") {
      setShowAddSiteAlert(true);
    } else {
      const response = await requestWithFeedback(() =>
        apiClient.post("sites/", {
          body: {
            name: siteName,
            country,
            coordinate,
            installation_date_new: installationDateNew,
            installation_date_used: installationDateUsed === "" ? null : installationDateUsed,
            panel_number: panelNumber,
            panel_price: panelPrice,
            output_per_panel: outputPerPanel,
            panel_output: panelOutput,
            timezone,
            power_dc_rated: powerDcRated,
            gamma_pdc: gammaPdc,
            tilt,
            azimuth,
            mu,
          },
        })
      );

      if (response === undefined) {
        return;
      }

      setCurrentEditSiteId("");
      setSiteName("");
      setCountry("");
      setCoordinate("");
      setInstallationDateNew("");
      setInstallationDateUsed("");
      setPanelNumber(0);
      setOutputPerPanel(0);
      setPanelOutput(0);
      setTimezone("Etc/GMT+7");
      setPowerDcRated(1000);
      setGammaPdc(-0.005);
      setTilt(40);
      setAzimuth(180);
      setMu(0.8);
      fetchSites();
    }
  }

  async function updateSite() {
    const response = await requestWithFeedback(() =>
      apiClient.put(`sites/${encodeURIComponent(currentEditSiteId)}/`, {
        body: {
          name: siteName,
          country,
          installation_date_new: installationDateNew,
          installation_date_used: installationDateUsed === "" ? null : installationDateUsed,
          panel_number: panelNumber,
          panel_price: panelPrice,
          output_per_panel: outputPerPanel,
          panel_output: panelOutput,
          timezone,
          power_dc_rated: powerDcRated,
          gamma_pdc: gammaPdc,
          tilt,
          azimuth,
          mu,
        },
      })
    );

    if (response === undefined) {
      return;
    }

    fetchSites();
  }

  async function deleteFile() {
    if (!filePendingDelete || deletingFile) {
      return;
    }

    const file = filePendingDelete;
    setDeletingFile(true);

    try {
      const response = await requestWithFeedback(() =>
        apiClient.delete(`files/${encodeURIComponent(file)}/`, {
          body: {
            systemType,
            file,
          },
          responseType: "none",
        })
      );

      if (response === undefined) {
        return;
      }

      setFilePendingDelete("");
      await fetchFiles();
    } finally {
      setDeletingFile(false);
    }
  }

  async function fetchFiles() {
    const response = await requestWithFeedback(() =>
      apiClient.get("files", {
        query: { systemType },
      })
    );

    if (response === undefined) {
      return;
    }

    setRunningDataFiles({
      columns: [
        { Header: t("siteManager.fileName"), accessor: "name" },
        { Header: t("siteManager.action"), accessor: "action", width: "10%" },
      ],

      rows: response.files.map((prop) => ({
        name: prop,
        action: (
          <div className="actions-right">
            <MDButton
              color="error"
              aria-label={t("siteManager.deleteFileAriaLabel", { file: prop })}
              onClick={() => handleDeleteFileOpen(prop)}
            >
              <Icon fontSize="medium" color="white">
                close
              </Icon>
            </MDButton>
          </div>
        ),
      })),
    });
  }

  async function fetchSites() {
    const response = await requestWithFeedback(() => apiClient.get("sites"));

    if (response === undefined) {
      return;
    }

    setSiteNumber(response.length);
    setTableData({
      columns: [
        { Header: t("siteManager.name"), accessor: "name" },
        { Header: t("siteManager.country"), accessor: "country" },
        { Header: t("siteManager.coordinate"), accessor: "coordinate" },
        { Header: t("siteManager.installationDateNew"), accessor: "installation_date_new" },
        { Header: t("siteManager.installationDateUsed"), accessor: "installation_date_used" },
        { Header: t("siteManager.panelOutput"), accessor: "panel_output" },
        { Header: t("siteManager.timezone"), accessor: "timezone" },
        { Header: t("siteManager.powerDcRated"), accessor: "power_dc_rated" },
        { Header: t("siteManager.gammaPdc"), accessor: "gamma_pdc" },
        { Header: t("siteManager.tilt"), accessor: "tilt" },
        { Header: t("siteManager.azimuth"), accessor: "azimuth" },
        { Header: t("siteManager.mu"), accessor: "mu" },
        { Header: t("siteManager.action"), accessor: "actions" },
      ],

      rows: response.map((prop, key) => ({
        id: key,
        name: prop.name,
        country: prop.country,
        coordinate: prop.coordinate,
        installation_date_new: prop.installation_date_new,
        installation_date_used: prop.installation_date_used,
        panel_output: prop.panel_output,
        timezone: prop.timezone,
        power_dc_rated: prop.power_dc_rated,
        gamma_pdc: prop.gamma_pdc,
        tilt: prop.tilt,
        azimuth: prop.azimuth,
        mu: prop.mu,
        actions: (
          <div className="actions-right">
            <MDButton
              onClick={() => {
                setCurrentEditSiteId(prop.id);
                setSiteName(prop.name);
                setCountry(prop.country);
                setCoordinate(prop.coordinate);
                setInstallationDateNew(prop.installation_date_new);
                setInstallationDateUsed(prop.installation_date_used);
                setPanelNumber(prop.panel_number);
                setPanelPrice(prop.panel_price);
                setOutputPerPanel(prop.output_per_panel);
                setPanelOutput(prop.panel_output);
                setTimezone(prop.timezone);
                setPowerDcRated(prop.power_dc_rated);
                setGammaPdc(prop.gamma_pdc);
                setTilt(prop.tilt);
                setAzimuth(prop.azimuth);
                setMu(prop.mu);
                handleEditOpen();
              }}
            >
              <Icon fontSize="medium" color="primary">
                edit
              </Icon>
            </MDButton>
          </div>
        ),
      })),
    });
  }

  useEffect(() => {
    fetchSites();
    fetchFiles();
  }, [i18n.resolvedLanguage]);

  async function caculateRequest() {
    handleLoadingOpen();
    const response = await requestWithFeedback(() =>
      apiClient.post("caculate_result", {
        body: {
          task_type: systemType,
        },
      })
    );
    handleLoadingClose();

    if (response !== undefined) {
      handleFinishOpen();
    }
  }

  const feedbackError = uploadError || requestError;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Collapse in={Boolean(feedbackError)}>
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                if (uploadError) {
                  setUploadError("");
                } else {
                  setRequestError("");
                }
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mx: 3, mt: 2 }}
        >
          {feedbackError}
        </Alert>
      </Collapse>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loadingOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Modal
        open={Boolean(filePendingDelete)}
        onClose={handleDeleteFileClose}
        aria-labelledby="delete-file-title"
        aria-describedby="delete-file-description"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Card sx={{ minWidth: 420, maxWidth: 560, p: 4, mx: 2 }}>
          <MDTypography id="delete-file-title" variant="h5">
            {t("siteManager.deleteFileTitle")}
          </MDTypography>
          <MDTypography id="delete-file-description" variant="body2" mt={2}>
            {t("siteManager.deleteFileConfirmation", { file: filePendingDelete })}
          </MDTypography>
          <Stack mt={4} spacing={2} direction="row" justifyContent="flex-end">
            <MDButton
              variant="outlined"
              color="dark"
              disabled={deletingFile}
              onClick={handleDeleteFileClose}
            >
              {t("siteManager.cancel")}
            </MDButton>
            <MDButton variant="gradient" color="error" disabled={deletingFile} onClick={deleteFile}>
              {deletingFile ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                t("siteManager.delete")
              )}
            </MDButton>
          </Stack>
        </Card>
      </Modal>
      <MDBox pb={3}>
        <Modal
          open={quotationOpen}
          onClick={handleQuotationClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox
            p={5}
            position="absolute"
            color="white"
            bgColor="white"
            transform="translate(-50%, -50%)"
          >
            <MDTypography variant="h5">
              {t("siteManager.quotationUsage", { days: dayNumber, dataPoints: siteNumber })}
            </MDTypography>
            <br />
            <MDTypography variant="h3">
              {t("siteManager.quotationBill", { amount: estimateBill(siteNumber, dayNumber) })}
            </MDTypography>
            <br />
            <MDTypography variant="h5">
              {t("siteManager.quotationDetails", {
                basic: estimateBasicFee(siteNumber),
                upload: estimateUploadData(dayNumber),
                transfer: estimateDataTransfer(dayNumber),
              })}
            </MDTypography>
            <br />
            <br />
            <br />
            <MDTypography variant="h5">
              <strong>{t("siteManager.quotationNoteLabel")}</strong>{" "}
              {t("siteManager.quotationNote")}
            </MDTypography>
            <br />
            <Stack spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="success"
                onClick={() => {
                  handleQuotationClose();
                }}
              >
                {t("siteManager.ok")}
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        <Modal
          open={generateFinished}
          onClick={handleFinishClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox
            p={5}
            position="absolute"
            color="white"
            bgColor="white"
            transform="translate(-50%, -50%)"
          >
            <MDTypography variant="h5">{t("siteManager.taskIssued")}</MDTypography>
            <br />
            <Stack spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  handleFinishClose();
                }}
              >
                {t("siteManager.ok")}
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        <Modal
          open={createOpen}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox p={5} position="absolute" transform="translate(-50%, -50%)">
            <MDBox pb={3}>
              <Collapse in={showAddSiteAlert}>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="error"
                      size="small"
                      onClick={() => setShowAddSiteAlert(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  {t("siteManager.inputError")}
                </Alert>
              </Collapse>
            </MDBox>
            <Card
              sx={{
                position: "relative",
                minWidth: "760px",
                mx: 3,
                py: 2,
                px: 4,
              }}
            >
              <MDTypography variant="h5">
                {t("siteManager.createNewSite")}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </MDTypography>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="text"
                  label={t("siteManager.name")}
                  defaultValue={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.country")}
                  defaultValue={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.coordinate")}
                  defaultValue={coordinate}
                  onChange={(e) => setCoordinate(e.target.value)}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="text"
                  label={t("siteManager.installationDateNewFormat")}
                  defaultValue={installationDateNew}
                  onChange={(e) => setInstallationDateNew(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.installationDateUsedFormat")}
                  defaultValue={installationDateUsed}
                  onChange={(e) => setInstallationDateUsed(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.panelNumber")}
                  defaultValue={panelNumber}
                  onChange={(e) => setPanelNumber(e.target.value)}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="number"
                  label={t("siteManager.panelPrice")}
                  defaultValue={panelPrice}
                  onChange={(e) => setPanelPrice(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.outputPerPanel")}
                  defaultValue={outputPerPanel}
                  onChange={(e) => setOutputPerPanel(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.panelOutput")}
                  defaultValue={panelOutput}
                  onChange={(e) => setPanelOutput(e.target.value)}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="number"
                  label={t("siteManager.powerDcRated")}
                  defaultValue={powerDcRated}
                  onChange={(e) => setPowerDcRated(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.gammaPdc")}
                  defaultValue={gammaPdc}
                  onChange={(e) => setGammaPdc(e.target.value)}
                />
                <Autocomplete
                  sx={{ minWidth: 220 }}
                  value={timezone}
                  onChange={(e, v) => setTimezone(v)}
                  inputValue={inputTimezone}
                  onInputChange={(e, v) => setInputTimezone(v)}
                  options={timezoneOptions}
                  renderInput={(params) => (
                    <MDInput {...params} variant="standard" label={t("siteManager.timezone")} />
                  )}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="number"
                  label={t("siteManager.tilt")}
                  defaultValue={tilt}
                  onChange={(e) => setTilt(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.azimuth")}
                  defaultValue={azimuth}
                  onChange={(e) => setAzimuth(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.mu")}
                  defaultValue={mu}
                  onChange={(e) => setMu(e.target.value)}
                />
              </Stack>
            </Card>
            <Stack mt={3} ml={5} spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  if (siteName === "" || country === "" || coordinate === "") {
                    setShowAddSiteAlert(true);
                  } else {
                    createSite();
                    handleCreateClose();
                  }
                }}
              >
                {t("siteManager.save")}
              </MDButton>
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  handleCreateClose();
                  setCurrentEditSiteId("");
                  setSiteName("");
                  setCountry("");
                  setCoordinate("");
                  setInstallationDateNew("");
                  setInstallationDateUsed("");
                  setPanelNumber(0);
                  setPanelPrice(0);
                  setOutputPerPanel(0);
                  setPanelOutput(0);
                  setTimezone("Etc/GMT+7");
                  setPowerDcRated(1000);
                  setGammaPdc(-0.005);
                  setTilt(40);
                  setAzimuth(180);
                  setMu(0.8);
                }}
              >
                {t("siteManager.close")}
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        <Modal
          open={editOpen}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox p={5} position="absolute" transform="translate(-50%, -50%)">
            <Card sx={{ position: "relative", minWidth: "760px", mt: -8, mx: 3, py: 2, px: 4 }}>
              <MDTypography variant="h5">{t("siteManager.editSite")}</MDTypography>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="text"
                  label={t("siteManager.name")}
                  defaultValue={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.country")}
                  defaultValue={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.installationDateNew")}
                  defaultValue={installationDateNew}
                  onChange={(e) => setInstallationDateNew(e.target.value)}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="text"
                  label={t("siteManager.installationDateUsed")}
                  defaultValue={installationDateUsed}
                  onChange={(e) => setInstallationDateUsed(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.panelNumber")}
                  defaultValue={panelNumber}
                  onChange={(e) => setPanelNumber(e.target.value)}
                />
                <FormField
                  type="number"
                  label={t("siteManager.panelPrice")}
                  defaultValue={panelPrice}
                  onChange={(e) => setPanelPrice(e.target.value)}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="number"
                  label={t("siteManager.outputPerPanel")}
                  defaultValue={outputPerPanel}
                  onChange={(e) => setOutputPerPanel(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.panelOutput")}
                  defaultValue={panelOutput}
                  onChange={(e) => setPanelOutput(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.powerDcRated")}
                  defaultValue={powerDcRated}
                  onChange={(e) => setPowerDcRated(e.target.value)}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="text"
                  label={t("siteManager.gammaPdc")}
                  defaultValue={gammaPdc}
                  onChange={(e) => setGammaPdc(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.tilt")}
                  defaultValue={tilt}
                  onChange={(e) => setTilt(e.target.value)}
                />
                <Autocomplete
                  sx={{ minWidth: 220 }}
                  value={timezone}
                  onChange={(e, v) => setTimezone(v)}
                  inputValue={inputTimezone}
                  onInputChange={(e, v) => setInputTimezone(v)}
                  options={timezoneOptions}
                  renderInput={(params) => (
                    <MDInput {...params} variant="standard" label={t("siteManager.timezone")} />
                  )}
                />
              </Stack>
              <Stack mt={3} spacing={5} direction="row">
                <FormField
                  type="text"
                  label={t("siteManager.azimuth")}
                  defaultValue={azimuth}
                  onChange={(e) => setAzimuth(e.target.value)}
                />
                <FormField
                  type="text"
                  label={t("siteManager.mu")}
                  defaultValue={mu}
                  onChange={(e) => setMu(e.target.value)}
                />
              </Stack>
            </Card>
            <Stack mt={3} ml={5} spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  updateSite();
                  handleEditClose();
                }}
              >
                {t("siteManager.yes")}
              </MDButton>
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  handleEditClose();
                }}
              >
                {t("siteManager.no")}
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        <Grid container alignItems="center" spacing={5}>
          <Grid item xs={4} md={4} lg={4}>
            <MDBox mb={1} position="relative" display="inline-flex" pr={5}>
              <MDButton variant="gradient" color="dark" onClick={() => caculateRequest()}>
                {t("siteManager.generateResult")}
              </MDButton>
              <HelpTooltip
                title={tHelp("siteManager.generateResults")}
                sx={{ position: "absolute", top: 0, right: 0 }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={4} md={4} lg={4}>
            <MDBox mb={1}>
              <MDButton variant="gradient" color="success" onClick={() => handleQuotationOpen()}>
                {t("siteManager.quotation")}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Card sx={{ width: "100%", position: "relative" }}>
            <HelpTooltip
              title={tHelp("siteManager.management")}
              sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
            />
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  assignment
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("siteManager.management")}
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3} alignItems="right">
                <Grid item xs={5} md={5} lg={5}>
                  <MDBox mb={1}> </MDBox>
                </Grid>
                <Grid item xs={5} md={5} lg={5}>
                  <MDBox mb={1}> </MDBox>
                </Grid>
                <Grid item xs={2} md={2} lg={2}>
                  <MDBox mb={1}>
                    <MDButton variant="gradient" color="dark" onClick={handleCreateOpen}>
                      {t("siteManager.addNew")}
                    </MDButton>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <DataTable table={tableData} canSearch />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
          <br />
        </MDBox>
        <Grid container alignItems="center" spacing={5}>
          <Grid item xs={12} md={12} lg={12}>
            <MDBox position="relative">
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDTypography variant="h5">{t("siteManager.runningData")}</MDTypography>
                <HelpTooltip title={tHelp("siteManager.runningData")} />
              </MDBox>
              <MDBox mt={3}>
                <MDBox
                  mb={1}
                  ml={0.5}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    {t("siteManager.runningData")}
                  </MDTypography>
                  <MDTypography
                    component="a"
                    href={`${process.env.PUBLIC_URL}/templates/data-file-template.csv`}
                    download="data-file-template.csv"
                    variant="button"
                    fontWeight="medium"
                    color="info"
                    sx={{ textDecoration: "underline" }}
                  >
                    {t("siteManager.downloadCsvTemplate")}
                  </MDTypography>
                </MDBox>
                {useMemo(() => {
                  const uploadTokens = new WeakMap();

                  return (
                    <MDDropzone
                      options={{
                        url: `${serverBaseURL}files/`,
                        XMLHttpRequest: true,
                        addRemoveLinks: false,
                        acceptedFiles: RUNNING_DATA_ACCEPTED_FILES,
                        maxFilesize: RUNNING_DATA_MAX_FILE_SIZE_MB,
                        dictDefaultMessage: t("siteManager.uploadDefaultMessage"),
                        dictInvalidFileType: t("siteManager.uploadInvalidType"),
                        dictFileTooBig: t("siteManager.uploadTooLarge", {
                          maxSize: RUNNING_DATA_MAX_FILE_SIZE_MB,
                        }),
                        init() {
                          this.on("addedfile", () => setUploadError(""));
                          this.on("complete", (file) => uploadTokens.delete(file));
                        },
                        async accept(file, done) {
                          try {
                            const accessToken = await getToken();

                            if (!accessToken) {
                              done("Unable to authenticate the upload. Please sign in again.");
                              return;
                            }

                            uploadTokens.set(file, accessToken);
                            done();
                          } catch (error) {
                            done("Unable to authenticate the upload. Please sign in again.");
                          }
                        },
                        headers: {
                          "Cache-Control": null,
                          "X-Requested-With": null,
                        },
                        sending(file, xhr) {
                          const accessToken = uploadTokens.get(file);
                          xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
                        },
                        params: (files, xhr, chunk) => {
                          if (chunk) {
                            return {
                              dzuuid: chunk.file.upload.uuid,
                              dzchunkindex: chunk.index,
                              dztotalfilesize: chunk.file.size,
                              dzchunksize: 2 * 1024 * 1024,
                              dztotalchunkcount: chunk.file.upload.totalChunkCount,
                              dzchunkbyteoffset: chunk.index * 2 * 1024 * 1024,
                            };
                          }

                          return {
                            systemType,
                          };
                        },
                        error(file, message) {
                          const uploadErrorMessage =
                            typeof message === "string"
                              ? message
                              : message?.detail ?? message?.message;

                          uploadTokens.delete(file);
                          setUploadError(uploadErrorMessage || t("siteManager.uploadFailed"));
                          this.removeFile(file);
                        },
                        success(file) {
                          this.removeFile(file);
                        },
                        queuecomplete() {
                          fetchFiles();
                        },
                      }}
                    />
                  );
                }, [getToken, i18n.resolvedLanguage, t])}
                <MDTypography variant="button" color="text" display="block" mt={1}>
                  {t("siteManager.uploadRequirements", {
                    maxSize: RUNNING_DATA_MAX_FILE_SIZE_MB,
                  })}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Card sx={{ width: "100%", position: "relative" }}>
            <HelpTooltip
              title={tHelp("siteManager.runningDataFiles")}
              sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
            />
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  assignment
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("siteManager.runningDataFiles")}
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <DataTable table={runningDataFiles} canSearch />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
          <br />
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default SiteManager;
